// src/errorReporting.js
// Tells the server when the app crashes in somebody's browser (see backend/client_errors.py), because nobody notices
// otherwise. Only the message, where in the code, the page without its query, the app version and the browser type
// are sent: no names, no tournaments, no account.
import { API_URL } from './config';

const MAX_PER_SESSION = 5;
// Things that are not bugs of ours: the browser or an extension being noisy, or simply no connection.
const IGNORED = [/ResizeObserver loop/i, /^Script error\.?$/i, /Failed to fetch/i, /NetworkError/i, /Load failed/i, /AbortError/i];

const sent = new Set();
let installed = false;

export const versionOfTheApp = () => process.env.REACT_APP_VERSION || process.env.REACT_APP_COMMIT || 'unknown';

// What is sent for an error: short text fields only.
export function buildReport(error, kind = 'error') {
    const message = String((error && error.message) || error || '');
    return {
        kind,
        message,
        stack: String((error && error.stack) || ''),
        page: `${window.location.origin}${window.location.pathname}`, // (no query, no fragment)
        version: versionOfTheApp(),
        browser: typeof navigator === 'undefined' ? '' : navigator.userAgent,
    };
}

export function shouldReport(report) {
    if (!report.message && !report.stack) return false;
    if (IGNORED.some((pattern) => pattern.test(report.message))) return false;
    const key = `${report.message}|${report.stack.split('\n')[1] || ''}`;
    if (sent.has(key) || sent.size >= MAX_PER_SESSION) return false;
    sent.add(key);
    return true;
}

export function reportError(error, kind) {
    try {
        const report = buildReport(error, kind);
        if (!shouldReport(report)) return;
        fetch(`${API_URL}/client-errors`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(report),
            keepalive: true,
        }).catch(() => {}); // reporting must never cause a problem of its own
    } catch (failure) {
        // nothing more to do
    }
}

// Catches what nothing else caught. Only in the real app, not in development or tests.
export function installErrorReporting() {
    if (installed || process.env.NODE_ENV !== 'production') return;
    installed = true;
    window.addEventListener('error', (event) => reportError(event.error || event.message, 'error'));
    window.addEventListener('unhandledrejection', (event) => reportError(event.reason, 'rejection'));
}

export const forgetReportedErrors = () => sent.clear(); // (for tests)
